import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { SharedModule } from "../../shared/shared.module";
import { HeaderComponent } from "./header/header.component";
import { MainComponent } from "./grid/main.component";
import {
  IPlugin,
  IUser,
  ModelService,
  PluginInjectorService,
  SessionFreshnessService,
  SessionService,
  IAllyService,
  ALLY_SERVICE,
} from "core-client/api";

@Component({
  selector: "app-dashboard",
  imports: [SharedModule, HeaderComponent, MainComponent],
  templateUrl: "./dashboard.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./dashboard.component.scss",
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  private allyService: IAllyService | null = inject(
    ALLY_SERVICE,
    { optional: true },
  );
  private readonly sessionFreshness = inject(SessionFreshnessService);
  loggedUser!: IUser;

  constructor(
    private readonly modelService: ModelService,
    private readonly sessionService: SessionService,
    private readonly pluginInjectorService: PluginInjectorService,
  ) {
    this.allyService?.init();
  }

  async ngOnInit(): Promise<void> {
    this.sessionFreshness.start();
    const initialUser = this.sessionService.user.getUser();
    if (initialUser) this.loggedUser = initialUser;
  }

  ngAfterViewInit(): void {
    this.sessionService.user
      .observeUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (user: IUser) => {
        this.loggedUser = user;
        await this.addAppGlobalSettings();
        await this.loadRegisteredPlugins();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private async addAppGlobalSettings(): Promise<void> {
    try {
      await this.modelService._appGlobalSettings.initialize();
    } catch (error) {
      console.error("Failed to initialize global settings:", error);
    }
  }

  private async loadRegisteredPlugins(): Promise<void> {
    await this.pluginInjectorService.whenPluginsReady();
    for (const registerPlugin of this.pluginInjectorService.getPlugins()) {
      this.notifyObservingPlugins(registerPlugin);
    }
  }

  private notifyObservingPlugins(plugin: IPlugin): void {
    this.modelService._plugin.notifyPlugin(plugin, "activate");
  }
}
